import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridToolbarProps,
} from "@mui/x-data-grid";
import { IBaseAgreementItem } from "src/interface/IBaseAgreementItem";
import { BaseAgreeType } from "src/interface/enum/BaseAgreeType";

interface CustomToolbarProps extends GridToolbarProps {
  setRows: (
    newRows: (oldRows: IBaseAgreementItem[]) => IBaseAgreementItem[]
  ) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
  rows: IBaseAgreementItem[];
  baseAgreeType: BaseAgreeType;
}

function EditToolbar(props: CustomToolbarProps) {
  const {
    setRows,
    setRowModesModel,
    rows,
    baseAgreeType: serviceItemType,
  } = props;

  const handleClick = () => {
    // پیدا کردن بزرگ‌ترین id موجود و اضافه کردن 1
    const maxId = rows.length > 0 ? Math.max(...rows.map((r) => r.id || 0)) : 0;
    const ID = maxId + 1;
    console.log("🚀 ~ handleClick ~ ID:", ID);

    const newRow: IBaseAgreementItem = {
      id: ID,
      typeItem: serviceItemType,
      title: "",
      value: 0,
      isNew: true,
    };

    // به‌روزرسانی rows و rowModesModel به صورت هماهنگ
    setRows((oldRows) => {
      const updatedRows = [...oldRows, newRow];
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [ID]: { mode: GridRowModes.Edit, fieldToFocus: "title" },
      }));
      return updatedRows;
    });
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        آیتم جدید
      </Button>
    </GridToolbarContainer>
  );
}

export default function BaseAgreementItems({
  rows: initialRows = [], // مقدار پیش‌فرض برای rows
  setRows,
  baseAgreeType,
}: {
  baseAgreeType: BaseAgreeType;
  rows?: IBaseAgreementItem[];
  setRows: React.Dispatch<React.SetStateAction<IBaseAgreementItem[]>>;
}) {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );

  // اطمینان از اینکه rows همیشه آرایه است
  const rows = initialRows || [];

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows((oldRows) => oldRows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      setRows((oldRows) => oldRows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows((oldRows) =>
      oldRows.map((row) => (row.id === newRow.id ? updatedRow : row))
    );
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "عنوان",
      minWidth: 150,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 3,
      editable: true,
      sortable: true,
    },
    {
      field: "value",
      headerName:
        baseAgreeType === BaseAgreeType.TypeActivity ? "ضریب" : "مبلغ",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "number",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      minWidth: 150,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{ color: "primary.main" }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="انصراف"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="ویرایش"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="حذف"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={rows
          .filter((r) => r.typeItem === baseAgreeType)
          .sort((a: any, b: any) => a.id - b.id)}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: EditToolbar as React.ComponentType<GridToolbarProps>,
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel, rows, baseAgreeType },
        }}
      />
    </Box>
  );
}
